import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import { granularityTag } from "../../charts/axis"
import { SERIES_COLORS } from "../../charts/palette"
import StackedBarChart from "../../charts/StackedBarChart"
import TrendLineChart from "../../charts/TrendLineChart"
import ChangeBadge from "../../components/Badges/ChangeBadge"
import KpiStrip from "../../components/KpiStrip/KpiStrip"
import MetricCard from "../../components/MetricCard/MetricCard"
import { Panel, PanelRow } from "../../components/Panel/Panel"
import { CHANNEL_OPTIONS, PROGRAM_OPTIONS } from "../../constants"
import { useCardData } from "../../hooks/useCardData"
import { getPercentChange, GOOD_DIRECTION } from "../../utils/change"
import { formatCount, formatMoney } from "../../utils/formats"

// The card's Tooltip terms, verbatim from the spec (§6 Revenue).
const INFO = [
  { name: "Revenue", formula: "Sum of charges recorded on lookups in the period, $" },
  { name: "Paid lookups", formula: "Lookups with a charge (fee_charged > 0)" },
  { name: "Change", formula: "In % against the comparison period" },
  { name: "By channel", formula: "Revenue in each interval by channel" },
  { name: "By program", formula: "Revenue separately for CC and OI" }
]

// The comparison note, the axes and the tooltips: "$" with no decimals (DAA-6-05, DAA-6-07).
const formatDollars = (value) => formatMoney(value, 0)

// Revenue (DAA-6-01 … DAA-6-08): how much Deflect earned, and through which channels and programs —
// revenue and paid lookups, revenue per channel (stacked) and per program over time. No copy states
// a price: each merchant's pricing scheme sets it (DAA-6-04). Every filter reaches it.
const RevenueCard = () => {
  const { t } = useTranslation()
  const { data, loading } = useCardData("revenue")
  const kpis = data?.kpis
  const comparison = data?.comparison

  const tiles = useMemo(() => {
    // Change badges only while comparing (DAA-2-22).
    const badge = (value, previous) =>
      comparison ? (
        <ChangeBadge change={getPercentChange(value, previous, GOOD_DIRECTION.UP)} />
      ) : null

    return [
      {
        key: "revenue",
        label: t("Revenue"),
        value: kpis?.revenue,
        prefix: "$",
        badge: badge(kpis?.revenue, comparison?.revenue),
        secondary: comparison
          ? `vs ${formatDollars(comparison.revenue)} ${t("prev. period")}`
          : null
      },
      {
        key: "paidLookups",
        label: t("Paid lookups"),
        value: kpis?.paid_lookups,
        badge: badge(kpis?.paid_lookups, comparison?.paid_lookups),
        secondary: comparison
          ? `vs ${formatCount(comparison.paid_lookups)} ${t("prev. period")}`
          : null
      }
    ]
  }, [kpis, comparison, t])

  // The billable channels the Channels filter keeps; Other carries no revenue (DAA-6-07).
  const channelSeries = useMemo(() => {
    const dollarsByChannel = data?.by_channel ?? {}

    return CHANNEL_OPTIONS.filter((option) => option.id in dollarsByChannel).map((option) => ({
      key: option.id,
      label: t(option.name),
      color: SERIES_COLORS[option.id],
      data: dollarsByChannel[option.id]
    }))
  }, [data, t])
  // Only the programs with revenue have a line, so a Program filter leaves its own (DAA-6-08), and a
  // period with lookups but no charge reads "No data available", as By channel beside it (DAA-1-18).
  const programSeries = useMemo(() => {
    const dollarsByProgram = data?.by_program ?? {}
    const hasRevenue = (option) => dollarsByProgram[option.value]?.some((dollars) => dollars > 0)

    return PROGRAM_OPTIONS.filter(hasRevenue).map((option) => ({
      key: option.value,
      label: option.label,
      color: SERIES_COLORS[option.value],
      data: dollarsByProgram[option.value]
    }))
  }, [data])

  return (
    <MetricCard title={t("Revenue")} info={INFO} loading={loading}>
      <KpiStrip tiles={tiles} />
      {/* Two panels of 6 under two KPIs, so their divider continues the KPIs' one (DAA-1-21). They keep
          their place until the first answer, so the page does not jump under them. Both carry a legend
          even for one series, by the panels' rule (DAA-6-07, DAA-6-08). */}
      <PanelRow>
        <Panel title={t("By channel")} tag={data && t(granularityTag(data.granularity))} span={6}>
          <div style={{ height: 260 }}>
            {data && (
              <StackedBarChart
                labels={data.labels}
                series={channelSeries}
                yFormat={formatDollars}
                yWhole
                legend
              />
            )}
          </div>
        </Panel>
        <Panel title={t("By program")} tag={data && t(granularityTag(data.granularity))} span={6}>
          <div style={{ height: 260 }}>
            {data && (
              <TrendLineChart
                labels={data.labels}
                series={programSeries}
                lastIntervalIncomplete={data.last_interval_incomplete}
                yFormat={formatDollars}
                yWhole
                // A lone program's line gets its area, as the design draws it.
                fill={programSeries.length === 1}
                legend
              />
            )}
          </div>
        </Panel>
      </PanelRow>
    </MetricCard>
  )
}

export default RevenueCard
